import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Lock, ShieldCheck, ArrowLeft } from "lucide-react";

import { requestPasswordReset, confirmPasswordReset } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AdminResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const token = params.get("token");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function requestLink(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await requestPasswordReset(email);
      setSent(true);
    } catch (err) {
      toast({ title: "Fout", description: err?.response?.data?.detail || "Probeer opnieuw", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  }

  async function confirmReset(e) {
    e.preventDefault();
    if (pw !== pw2) {
      toast({ title: "Wachtwoorden komen niet overeen", variant: "destructive" });
      return;
    }
    if (pw.length < 10) {
      toast({ title: "Wachtwoord moet minstens 10 tekens hebben", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      await confirmPasswordReset(token, pw);
      toast({ title: "Wachtwoord gewijzigd", description: "Je kan nu inloggen met je nieuwe wachtwoord." });
      navigate("/admin/login");
    } catch (err) {
      toast({ title: "Fout", description: err?.response?.data?.detail || "Token verlopen of ongeldig", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center px-6" data-testid="admin-reset-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <span className="inline-flex h-10 w-10 rounded-xl bg-blue-500 items-center justify-center text-white">
            <ShieldCheck className="h-6 w-6" />
          </span>
          <span className="font-display text-2xl font-bold tracking-tight text-white">
            Check<span className="text-blue-300">Expert</span>
          </span>
        </div>

        {token ? (
          <form onSubmit={confirmReset} className="rounded-2xl bg-white p-8 space-y-5 shadow-2xl">
            <h1 className="font-display text-xl font-bold text-ink">Nieuw wachtwoord instellen</h1>
            <div>
              <Label>Nieuw wachtwoord</Label>
              <Input type="password" required value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-pw" />
            </div>
            <div>
              <Label>Bevestig wachtwoord</Label>
              <Input type="password" required value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="reset-pw2" />
            </div>
            <button type="submit" disabled={submitting} className="w-full px-5 py-3 rounded-full bg-[#002FA7] hover:bg-[#0033B5] text-white font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="reset-confirm">
              <Lock className="h-4 w-4" /> {submitting ? "Bezig…" : "Wachtwoord wijzigen"}
            </button>
          </form>
        ) : sent ? (
          <div className="rounded-2xl bg-white p-8 shadow-2xl text-center" data-testid="reset-sent">
            <h1 className="font-display text-xl font-bold text-ink">Mail verzonden</h1>
            <p className="text-ink-soft mt-3 text-sm">
              Als dit e-mailadres bekend is, ontvang je binnen enkele minuten een reset-link. De link is 2 uur geldig.
            </p>
            <Link to="/admin/login" className="mt-6 inline-flex items-center gap-1 text-sm text-[#002FA7] font-semibold">
              <ArrowLeft className="h-4 w-4" /> Terug naar login
            </Link>
          </div>
        ) : (
          <form onSubmit={requestLink} className="rounded-2xl bg-white p-8 space-y-5 shadow-2xl">
            <h1 className="font-display text-xl font-bold text-ink">Wachtwoord vergeten?</h1>
            <p className="text-ink-soft text-sm">Vul je e-mail in. We sturen een reset-link.</p>
            <div>
              <Label>E-mail</Label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="reset-email" />
            </div>
            <button type="submit" disabled={submitting} className="w-full px-5 py-3 rounded-full bg-[#002FA7] hover:bg-[#0033B5] text-white font-semibold disabled:opacity-50" data-testid="reset-request">
              {submitting ? "Bezig…" : "Verstuur reset-link"}
            </button>
            <Link to="/admin/login" className="block text-center text-sm text-[#002FA7] font-semibold mt-4">
              ← Terug naar login
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
